import { useMemo } from 'react';
import Fuse from 'fuse.js';
import type { CommandPaletteSearchType, CommandPaletteSearchFEResult } from '../../../../shared/CommandPalette/types';

const SECTION_LIMIT = 5;

/**
 * Takes the full list of filtered results, and limits each type to SECTION_LIMIT.
 *
 * @param {CommandPaletteSearchFEResult[]} filteredResults - The full list of filtered results.
 * @returns {CommandPaletteSearchFEResult[]} The filtered results, limited by type.
 */
function limitResultsByType(filteredResults: CommandPaletteSearchFEResult[]): CommandPaletteSearchFEResult[] {
  const resultCountsByType: Record<CommandPaletteSearchType, CommandPaletteSearchFEResult[]> = {
    staticPage: [],
    project: [],
    releaseGroup: [],
  };

  filteredResults.forEach((result) => {
    const results = resultCountsByType[result.type];
    if (results.length < SECTION_LIMIT) {
      results.push(result);
    }
  });

  return Object.values(resultCountsByType).flat();
}

/**
 * Use the search query filter the static routes, as well as the search results.
 *
 * @param {string} searchQuery - The search query to filter the results by.
 * @param {CommandPaletteSearchFEResult[]} staticRoutes - The static routes to filter.
 * @param {CommandPaletteSearchFEResult[]} searchResults - The search results to filter.
 * @returns {CommandPaletteSearchFEResult[]} The filtered results.
 */
function useFilteredResults(
  searchQuery: string,
  staticRoutes: CommandPaletteSearchFEResult[],
  searchResults: CommandPaletteSearchFEResult[],
): CommandPaletteSearchFEResult[] {
  return useMemo(() => {
    const allRoutes = [...staticRoutes, ...searchResults];

    const fuse = new Fuse(allRoutes, {
      keys: ['label', 'to'],
      isCaseSensitive: false,
    });

    //  If there is a search query, filter the results.
    //  We do this because fuse will return an empty array if the search query is empty.
    const filteredResults = searchQuery
      ? fuse.search(searchQuery).map((result) => result.item)
      : allRoutes;

    return limitResultsByType(filteredResults);
  }, [searchQuery, staticRoutes, searchResults]);
}

export default useFilteredResults;
